/**
 * analytics.ts — «Аналитика», «Лиды», «Когорты» и дашборд организации
 * (WEB-1874, волна переноса, пачка 15).
 *
 * ОДИН СЛОВАРЬ НА ЧЕТЫРЕ ЭКРАНА — намеренно. Дашборд организации это та же
 * таблица показателей, что и проектная «Статистика», только разрезанная по
 * проектам: подписи колонок («Показы», «Уники», «Квиз 90%») там обязаны
 * совпадать дословно, и до этой карточки они держались двумя ручными
 * списками (`ColumnsToggle.COLUMN_LABELS` и `orgProjectMetrics`), которые уже
 * сверялись отдельным тестом. Второй словарь вернул бы ровно ту же
 * «разошедшуюся копию правила»: подпись поправили бы в одном месте из двух.
 *
 * Журналы («Аудит») сюда НЕ входят: у них своя лексика действий и свой
 * словарь `audit.ts` — иначе файл перевалил бы за тысячу строк.
 *
 * НАЗВАНИЯ СТРАН. Обе половины берут название из общего справочника
 * `constants/countries.ts` (249 стран, в каждой записи пара ru/en) — своей
 * копии названий тут нет ни на одном языке. До WEB-2168 русская половина
 * жила отдельной картой на 35 стран и молча деградировала к английскому.
 */
import { COUNTRY_OPTIONS } from '@/constants/countries';
import { defineDictionary } from '../define';
import { plural } from '../plural';

/** Английское название страны по коду — из общего справочника, без копии. */
const ENGLISH_COUNTRY_NAMES = new Map(
  COUNTRY_OPTIONS.map((c) => [c.value, c.label] as const),
);

/**
 * Русское название страны по коду — ИЗ ТОГО ЖЕ справочника (WEB-2168).
 *
 * Раньше здесь лежала своя карта на 35 стран из 249: русский кабинет молча
 * показывал остальные по-английски, а добавить страну надо было в двух местах
 * — то есть это был реестр с двумя концами, который и разъехался. Теперь пара
 * названий живёт одной записью в `constants/countries.ts`, и у обеих половин
 * словаря здесь — один источник.
 */
const RUSSIAN_COUNTRY_NAMES = new Map(
  COUNTRY_OPTIONS.map((c) => [c.value, c.labelRu] as const),
);

export const analyticsStrings = defineDictionary(
  {
    // ── Подписи колонок показателей (общие с дашбордом организации) ────────
    // WEB-2420 Д2 (решение владельца 23.09, «Переименовать везде»): показы —
    // «Views», не «Clicks» (кликов колонка не считает).
    colViews: 'Views',
    colUniques: 'Sessions',
    colUniqueVisitors: 'Uniques',
    colQuizStarts: 'Quiz starts',
    colQuizStartRate: 'Quiz start rate',
    colPageviews: 'Pageviews',
    colRegistrations: 'Registrations',
    colPurchases: 'Purchases',
    colFailedCheckouts: 'Failed payments',
    colRevenue: 'Revenue',
    colQuiz50: 'Quiz 50%',
    colQuiz90: 'Quiz 90%',
    colPaywallViews: 'Paywall',
    colInstalls: 'Installs',
    colTraffback: 'Traffback',
    colArpu: 'ARPU',
    colArppu: 'ARPPU',
    colConvBuy: 'Purchase conversion',
    /** WEB-4127: таблица «Проекты» дашборда организации (эталон org-dash-x.jsx:161, :164); общие колонки выше — у «Статистики». */
    orgColVisitors: 'Visitors',
    orgColConversion: 'Conversion',
    colConversionToApp: '% to app',

    // ── Настройка колонок ──────────────────────────────────────────────────
    columns: 'Columns',
    cohortsHiddenOwnMoney:
      'Cohorts are hidden: they are counted by buyers. Change it in the project settings.',
    derivedColumnsToggle: 'ARPU / ARPPU / conversion',
    columnsOwnMoneyNote:
      'There are no money columns in the list: you take payments yourself. Change it in the project settings.',
    columnsReorderHint:
      'Drag rows to change the order. The CSV export contains exactly the visible columns, in this order.',
    dragToSort: (label: string) => `Drag to sort: ${label}`,

    // ── Таблица статистики ─────────────────────────────────────────────────
    directTrafficNoUtm: 'Direct traffic / no UTM',
    // WEB-3740 юнит 53-1: пустые строки «Воронки» и «UTM-канала», интервалы
    // «Времени суток» (эталон analytics-dims.jsx:28; сервер отдаёт ключ).
    rowNoFunnel: 'No funnel',
    // Решение владельца 02.11 п.24: почему сумма строк «Воронки» больше «Итого».
    funnelRowsOverTotal:
      'One paywall can belong to several funnels: its visitors and payments count in the row of each, so the rows can add up to more than the Total.',
    rowNoChannel: 'No channel',
    todNight: 'Night, 0–6',
    todMorning: 'Morning, 6–12',
    todAfternoon: 'Afternoon, 12–18',
    todEvening: 'Evening, 18–24',
    // WEB-2268: подпись строки разреза по виду (dimensionLabelKind сервера);
    // имя — из dimensionLabel, клиентское имя не переводится.
    rowKindApex: (_name: string) => 'Main domain',
    rowKindUnattributed: (_name: string) => 'Not attributed',
    rowKindDirect: (_name: string) => 'Direct',
    rowKindDeletedVariant: (id: string) => `Deleted variant (${id})`,
    rowKindDeletedSmartlink: (id: string) => `Deleted A/B test (${id})`,
    rowKindDeletedDomain: (id: string) => `Deleted domain (${id})`,
    rowKindDeletedMember: (id: string) => `Deleted member (${id})`,
    rowKindQuiz: (name: string) => `Quiz “${name}”`,
    rowKindWholeFunnel: (name: string) => `Funnel “${name}”`,
    rowKindUrl: (address: string) => `URL ${address}`,
    dimension: 'Dimension',
    total: 'Total',
    refresh: 'Refresh',
    exportCsv: 'Export CSV',
    statsLoadFailed: 'Could not load the statistics',
    noDataYet: 'No data yet',
    noDataYetHint: 'Publish the funnel and drive some traffic',
    trafficEmptyTitle: 'Data will appear once traffic starts',

    // ── Воронка прохождения на экране статистики ───────────────────────────
    funnelLoadFailed: 'Could not load the funnel',
    /**
     * WEB-1368 п.4.5 — подписи шагов воронки. Раньше шаг печатался кодом
     * события с заменой подчёркиваний на пробелы («1. QUIZ VIEW»): коды на
     * сервере заглавные, то есть на экране стояла машинная строка.
     * Множество ключей сверяется с FUNNEL_ORDER самого сервера сторожем
     * FunnelView.stepLabels.web1368.test.tsx (второй конец выводится из
     * правды, а не из списка руками).
     */
    funnelStepQuizView: 'Funnel opened',
    funnelStepQuizStart: 'Quiz started',
    funnelStepQuizComplete: 'Quiz completed',
    funnelStepPaywallView: 'Paywall shown',
    funnelStepCheckoutStart: 'Checkout started',
    funnelStepCheckoutSuccess: 'Payment completed',
    funnelStepInstalls: 'Installs',
    funnelStepPaywallHandoff: 'Left for checkout',
    funnelMoneyStepsHidden:
      'Paywall, payment and install steps are hidden: you take payments yourself. The last step we can measure is the moment we send a person to your checkout page.',
    funnelCaption: 'Funnel for the period and the selected filters · unique sessions:',
    convFromPrevStep: 'Conversion from the previous step',
    peopleWord: (n: number) => plural('en', n, { one: 'person', other: 'people' }),
    ofPrevStep: (pct: string) => `${pct}% of prev. step`,

    // ── Период ─────────────────────────────────────────────────────────────
    presetToday: 'Today',
    presetLast7Days: 'Last 7 days',
    presetLast30Days: 'Last 30 days',
    presetAllTime: 'All time',
    presetLast7DaysShort: '7 days',
    presetLast30DaysShort: '30 days',
    rangeStart: 'Start',
    rangeEnd: 'End',

    // ── Фильтры ────────────────────────────────────────────────────────────
    countryName: (code: string) => ENGLISH_COUNTRY_NAMES.get(code) ?? code,
    groupNone: 'No grouping',
    groupCountry: 'Country',
    groupDate: 'Date',
    groupUtmSource: 'UTM source',
    groupUtmCampaign: 'UTM campaign',
    groupQuiz: 'Quiz',
    groupPaywall: 'Paywall',
    groupDomain: 'Domain',
    groupMember: 'Member',
    groupVariant: 'Direction',
    groupSmartLink: 'A/B test',
    // ── Разрезы аналитики нового вида (WEB-3752, components/statistics/ds) ──
    dimFunnel: 'Funnel',
    dimQuiz: 'Quiz',
    dimPaywall: 'Paywall',
    dimUtmSource: 'UTM source',
    dimUtmCampaign: 'UTM campaign',
    dimUtmMedium: 'UTM medium',
    dimCountry: 'Country',
    dimDomain: 'Domain',
    dimMember: 'Member',
    dimVariant: 'A/B variant',
    dimAbTest: 'A/B test',
    dimLanguage: 'Language',
    dimDevice: 'Device',
    dimOs: 'OS',
    dimTimeOfDay: 'Time of day',
    dimDay: 'Day',
    // WEB-3752 юнит 2: шапка, пустая аналитика и общая панель фильтров
    // (эталон analytics-x.jsx, TX и FV).
    lensFlow: 'Funnel',
    lensGroupLabel: 'Lens',
    emptyTitle: 'No data yet',
    emptyText: 'Analytics appears after the first funnel visitors',
    emptyBuild: 'Build a funnel',
    filterTestPayments: 'Test payments',
    filterTestExcluded: 'excluded',
    filterTestIncluded: 'included',
    filterMore: 'More filters',
    filterLess: 'Hide empty',
    filterReset: 'Reset',
    filterAnswerRemove: 'Remove filter',
    fieldPeriod: 'Period',
    fieldUtmSource: 'UTM source',
    fieldUtmCampaign: 'UTM campaign',
    fieldUtmMedium: 'UTM medium',
    fieldGroupBy: 'Group by',
    fieldQuizzes: 'Quizzes',
    fieldPaywalls: 'Paywalls',
    fieldMembers: 'Members',
    fieldAbTests: 'A/B tests',
    fieldCountries: 'Countries',
    fieldDomains: 'Domains',
    fieldProjects: 'Projects',
    fieldFunnels: 'Funnels',
    fieldSources: 'Sources',
    // WEB-2215: список источников когорт приходит с сервера и у проекта без
    // размеченного трафика пуст по-честному — общее «Нет вариантов» тут ничего
    // не объясняет.
    cohortSourcesEmpty: 'No tagged traffic yet',
    // WEB-2239: своя подпись пустого списка у каждого фильтра когорт.
    cohortOptionsEmpty: 'Nothing in this project yet',
    // WEB-2238: страны — выбором из данных проекта.
    cohortCountriesEmpty: 'No visits with a known country yet',
    // WEB-2405 пп.12/19: расход введён на весь трафик, окупаемость среза не считается.
    cohortRoiFilteredNote:
      'Spend is entered for all traffic, so the payback of a filtered slice is not calculated. Clear the filters to see ROI.',
    cohortSpendNoCurrencyNote:
      'Spend and ROI are hidden: the revenue currency is unknown or there are several, so it cannot be compared with one spend figure.',
    // Ревизия пачки 49: расход хранится на каждую валюту выручки отдельно.
    cohortSpendPerCurrencyNote:
      'Revenue in this report comes in several currencies. Spend is entered separately for each currency, and ROI compares it with revenue in that currency only.',
    allQuizzes: 'All quizzes',
    allPaywalls: 'All paywalls',
    allMembers: 'All members',
    allAbTests: 'All A/B tests',
    allCountries: 'All countries',
    allDomains: 'All domains',
    allFunnels: 'All funnels',
    allProjects: 'All projects',
    all: 'All',
    showTestPayments: 'Show test payments',
    filtersCombineWithAnd: 'Filters combine with AND.',

    // ── Быстрые отчёты ─────────────────────────────────────────────────────
    quickReports: 'Quick reports',
    reportCountries30: 'Countries, last 30 days',
    reportUtmCampaigns7: 'UTM campaigns, last 7 days',
    reportSmartLinks30: 'A/B tests, last 30 days',
    reportMembers30: 'By member, last 30 days',
    viewGeneral: 'Overall',
    viewDaily: 'By day',


    /**
     * Имена месяцев для КОМПАКТНЫХ подписей («29 июн – 5 июл», «Июнь 2026»).
     * Почему не `Intl`: он даёт «июн.» с точкой и «июнь 2026 г.» со строчной
     * буквы — ярлык когорты и заголовок журнала свёрстаны под короткую форму
     * без точки, и переход на `Intl` сдвинул бы ратифицированную ширину
     * колонки. Полные даты (`formatDate`) по-прежнему печатает `Intl`; здесь
     * ровно тот случай, когда нужна своя короткая форма.
     */
    monthShort: (index: number) =>
      ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][index] ?? '',
    monthFull: (index: number) =>
      [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
      ][index] ?? '',

    // ── Показатели лидов ───────────────────────────────────────────────────
    metricRevenue: 'Revenue',
    metricBuyers: 'Buyers',
    metricVisitors: 'Visitors',
    metricQuizStarts: 'Quiz starts',
    metricLeftEmail: 'Left an email',
    metricFinishedQuiz: 'Finished the quiz',
    metricCheckoutStarts: 'Started checkout',
    // WEB-2126: показатель для проекта, который принимает деньги сам.
    metricPaywallHandoff: 'Left to pay',
    metricPaywallHandoffHint:
      'How many people we sent to your own payment page. It is the last step ' +
      'we can see: what happens on your side is not ours to count.',
    baseMetrics: 'Key metrics',
    conversions: 'Conversions',
    convVisitorToQuizStart: 'Visitor → Quiz start',
    convVisitorToQuizFinish: 'Visitor → Quiz finish',
    convQuizStartToFinish: 'Quiz start → Quiz finish',
    convPaywallToCheckout: 'Paywall → Checkout start',
    convCheckoutToSubscription: 'Checkout start → Subscription',
    convPaywallToSubscription: 'Paywall → Subscription',
    convVisitorToPurchase: 'Visitor → Purchase',
    convFinishToHandoff: 'Quiz finish → Left to pay',
    totalLtv: 'Total LTV',
    totalLtvHint:
      'How much money one person of a cohort brings over its whole lifetime ' +
      '(from the cohort matrix: renewals and refunds included, the inflow window ' +
      'is not limited by the selected period). It does not have to match ' +
      '“Revenue” for the period.',
    retention: 'Retention',
    // ── Список лидов ───────────────────────────────────────────────────────
    leadsScopedNotice:
      'You only see leads from your own quizzes and paywalls; leads with no ' +
      'resource attached are visible to the project owner, an administrator and ' +
      'a retention manager. The CSV export follows the same narrowed list. The ' +
      'counters above are narrowed too, but cover the last 30 days, and ' +
      'subscriptions are not shown to a member at all — they do not have to add ' +
      'up to the “total” under the table.',
    leadsExportScopedNotice:
      'The export is narrowed to your leads: entries from other people’s quizzes ' +
      'and paywalls, as well as those with no resource attached, are not in the file.',
    leadsExported: 'Leads exported',
    leadsLoadFailed: 'Could not load the leads',
    firstTouchTooltip:
      'First touch: the lead’s first visit is kept; later visits do not overwrite the UTM',
    leadQuiz: 'Quiz',
    leadPaywall: 'Paywall',
    leadCreated: 'Created',
    leadLastVisit: 'Last visit',
    leadSearchPlaceholder: 'Search by email (partial match)',
    leadsEmptyHint: 'Drive views to the funnel — the first leads will show up automatically.',

    // ── Сводка по подпискам ────────────────────────────────────────────────
    subscriptionsNoAccess:
      'No access to the subscriptions summary. The section is available to owners, administrators and the project RM.',
    subscriptionsLoadFailed: 'Could not load the subscriptions summary',
    // WEB-2405 п.14: сводка подписок фильтров соседней панели не слушает.
    subscriptionsWholeProjectNote: 'Across the whole project, filters not applied',
    subscriptions: 'Subscriptions',
    subscriptionsNew: 'New subscriptions',
    subscriptionsActive: 'Active subscriptions',
    subscriptionsCanceled: 'Canceled subscriptions',
    subscriptionsRefunded: 'Subscription refunds',

    // ── Когорты ────────────────────────────────────────────────────────────
    cohortsLoadFailed: 'Could not load the cohorts',
    cohortGranularity: 'Granularity',
    cohortGranularityDay: 'Day',
    cohortGranularityWeek: 'Week',
    cohortGranularityMonth: 'Month',
    cohortMetric: 'Metric',
    cohortMetricLtv: 'LTV per user',
    cohortMetricRetention: 'Retention',
    cohortMetricConversion: 'Conversion',
    cohortView: 'View',
    cohortViewTable: 'Table',
    cohortViewChart: 'Chart',
    cohortHistoryHint: 'History is kept for 12 months · filters combine with AND.',
    cohortsIntro:
      'Traffic payback: cohorts by first-touch date. Spend and ROI are per cohort ' +
      '(spend is saved in the project — teammates see it too).',
    cohortsEmptyHint: 'Drive views to the funnel — the first cohorts will show up automatically.',
    cohortCurvesAria: 'Cohort curves by age',
    cohortNotOldEnough: 'The cohort has not reached this age yet',
    cohort: 'Cohort',
    cohortSize: 'Size',
    cohortRefundsNote:
      'Refunds are already deducted from LTV — there is no need to count them again. They do not come as a separate column: the server does not report refunds per cohort.',
    cohortSpend: 'Spend',
    cohortSpendTooltip:
      'The budget spent on this cohort’s traffic (entered by hand, saved in the project — teammates see it too)',
    cohortRoiTooltip: '(mature LTV per user x cohort size) / spend',
    cohortAverage: 'Average',
    cohortHeatLegend: 'Fill: brighter = higher',
    cohortNotLivedYet: 'not old enough yet',
    cohortDayZero: 'Day 0',
    /** WEB-4127: «Удержание», недельная разбивка — возраст в неделях (эталон analytics-ret.jsx:36). */
    cohortWeekAge: (n: number) => `W${n}`,

    // ── Дашборд организации ────────────────────────────────────────────────
    orgVisibleColumns: 'Visible columns',
    orgProject: 'Project',
    orgEmptyHint: 'Drive views to the projects’ funnels — the summary will build itself.',
    // Пачка 49 (WEB-2405 Г2): отбор, под который ничего не попало, — не «трафика нет».
    orgFilterEmptyTitle: 'Nothing matches these filters',
    orgFilterEmptyHint: 'Change or reset the filters above.',
    // WEB-2420 Д1 (решение владельца 2387-Р16): пока сервер не исключает
    // посетителей проектов «деньги у себя» из знаменателей (волна 2), плашки
    // говорят, что покупки таких проектов не учитываются. Не «без N проектов» —
    // их посетители в расчёте есть.
    orgOwnMoneyNote: (n: number) =>
      `Purchases of projects that take payments themselves (${n}) are not counted here — their visitors still count toward conversions and ARPU.`,
    // WEB-2404 Д1/Г2: правило подсчёта — люди с идентификатором плюс сессии
    // показов без него (посетитель не дал согласия). WEB-2644: подпись о
    // завышении при согласии посреди посещения снята — сервер помечает такие
    // показы, человек считается один раз (uniqueVisitorsHint.ts).
    orgUniquesHint:
      'People with a visitor identifier plus quiz or pricing screen view sessions ' +
      'without one (the visitor did not consent to tracking). Service sessions do ' +
      'not count: every click on an A/B test link starts a separate session, and ' +
      'without this correction one person who clicked twice would count as two.',
    orgAnalyticsNoAccess:
      'No access to analytics. The section is available to organization owners and administrators only.',
    orgSwitchProjectFailed: 'Could not switch the project',
    orgDashboard: 'Dashboard',
    // ── Выручка: что именно посчитано ──────────────────────────────────────
    revenueHeaderTooltip:
      'Rows show the first payments received, sliced by the dimension. “Total” is the project’s net revenue, renewals and deductions included.',
    revenueHintFirstPaymentsOnly: 'First payments for the selected period.',
    revenueHintNoRenewalsInSlice:
      'This slice shows first payments only: renewals and refunds arrive without a ' +
      'country, UTM, quiz or member — there is nothing to slice them by.',
    revenueHintNet:
      'Counted for the selected period: first payments + renewals − refunds − lost ' +
      'disputes. An open dispute is not deducted until it is resolved. It does not ' +
      'have to match “Total LTV” by definition — that one is a cohort’s money over ' +
      'its whole lifetime.',
    // WEB-2203 п.2 — решение владельца 25.08 объяснено ТАМ, ГДЕ человек видит
    // ноль: подписки, перенесённые из прежней платёжной системы, отчётностью
    // не считаются. Само решение карточкой не пересматривается — только его
    // видимость на экране.
    revenueHintImportedSubscriptions:
      ' Subscriptions carried over from your previous payment system are not ' +
      'counted here: those payments went through someone else’s checkout, and ' +
      'counting them would double your history. The subscribers themselves are ' +
      'visible and their access works.',
    revenueHintMixedCurrency:
      ' No currency sign is shown: the selection holds several currencies (or there ' +
      'are no sales). Amounts in different currencies are not converted — they are ' +
      'summed as they are.',
    // WEB-2203 п.1 — расхождение «Итого» с суммой строк, подписанное В ОТЧЁТЕ
    // (а не подсказкой под курсором): с числами, чтобы калькулятор сошёлся.
    revenueTotalVsRowsNote: (renewals: string, deductions: string) =>
      `“Total” is not the sum of the rows above: renewals of ${renewals} are added ` +
      `to the first payments and ${deductions} of refunds and lost disputes are ` +
      'subtracted. That money arrives without a country, UTM, quiz or member — ' +
      'there is nothing to spread it over the rows by.',
    // Ревизия 43.1: при нескольких валютах сумм в подписи нет — складывать
    // иены с долларами нечестно, а делить их на 100 одним правилом неверно.
    revenueTotalVsRowsNoteNoAmounts:
      '“Total” is not the sum of the rows above: renewals are added to the first ' +
      'payments, refunds and lost disputes are subtracted. That money arrives ' +
      'without a country, UTM, quiz or member — there is nothing to spread it ' +
      'over the rows by.',
    // WEB-2402 Д1 / WEB-2405 Д1 (решение владельца 2387-Р4, пачка 49, В-13):
    // деньги в нескольких валютах показываются строкой на валюту, общего числа
    // нет — прежняя приписка «суммы сложены как есть» при раскладке неправда.
    revenueHintByCurrency:
      ' Money in different currencies is shown separately, one line per ' +
      'currency: there is no single total, amounts are not converted.',
    // Строка раскладки без кода валюты: наименьшие единицы известны, а
    // множитель — нет, поэтому суммы не печатаем.
    revenueNoCurrencyCode: 'no currency code',
    revenueTotalVsRowsNoteByCurrency: (parts: string) =>
      '“Total” is not the sum of the rows above: renewals are added to the ' +
      `first payments, refunds and lost disputes are subtracted, per currency — ${parts}. ` +
      'That money arrives without a country, UTM, quiz or member — there is ' +
      'nothing to spread it over the rows by.',
    revenueTotalVsRowsCurrencyPart: (currency: string, renewals: string, deductions: string) =>
      `${currency}: renewals ${renewals}, deductions ${deductions}`,
    // Колонка выгрузки и подпись переключателя валюты когорт.
    currencyColumn: 'Currency',
    cohortPickCurrency: 'Select a currency',
    cohortPickCurrencyNote:
      'Cohort revenue is in several currencies — select a currency above to see ' +
      'LTV: amounts in different currencies are not added up.',

    // ── Страница «Аналитика» ───────────────────────────────────────────────
    pageTitle: 'Analytics',

    // ── Вкладка «Ответы» (WEB-2093) ────────────────────────────────────────
    tabAnswers: 'Answers',
    answersTitle: 'Answer distribution',
    answersLoading: 'Collecting answers…',
    // WEB-2405 п.21: «Всё время» у этого отчёта невозможно — сервер требует период.
    answersLoadFailed: 'Could not load the answer distribution.',
    answersEmpty: 'No answers for this period yet',
    answersEmptyHint:
      'Widen the period or relax the filters. Answers appear once visitors pass ' +
      'a screen with a choice question.',
    answersTruncated:
      'The list is cut at the row limit: narrow the period or the filters to see the rest.',
    answersColOption: 'Answer',
    answersColRespondents: 'Answered',
    answersColShare: 'Share',
    answersQuestionFallback: (blockId: string) => `Question ${blockId}`,
    answersQuestionTotal: (count: string) => `${count} people answered this question`,
    // WEB-2566 п.1: подсказка у недоступной выгрузки когорт без выбранной валюты.
    cohortExportPickCurrency: 'Select a currency to export',
    // WEB-3752 юнит 3: река шагов, «Шаги», «Сравнить», «По дням»,
    // «Группировать по», «Обновить» (эталон analytics-x.jsx: TX, ST, TT;
    // подпись «Сегодня» — noHours главной home-x.jsx, решение владельца 27.10 № 11).
    stepsBtn: 'Steps',
    stepsShown: 'Steps in order',
    stepsAdd: 'Add a step',
    stepsMin: 'At least 3',
    stepsMax: 'At most 8',
    stepsColumn: (name: string) => `column «${name}»`,
    stepsBar: 'Funnel steps',
    stepsHint: 'Percentages are from the previous step',
    stepsUp: 'Up',
    stepsDown: 'Down',
    stepsDrag: 'Drag to reorder',
    stepsReset: 'Reset to default',
    stepLeftEmail: 'Left an email',
    stepColFinished: 'Finished the quiz',
    stepColPaywallUniques: 'Saw the paywall, people',
    stepColCheckoutStarts: 'Started checkout',
    compare: 'Compare',
    compareTip: 'Show the previous period next to the current one',
    breakdownToFilter: 'to filter',
    breakdownConv: 'conv.',
    dataRefreshed: 'Data refreshed',
    // WEB-4119 (сверка вида 31.10 № 3): «Выгрузить» не гаснет, как у эталона; файла нет — эта всплывашка.
    exportNothing: 'Nothing to export: this tab has no data, or it failed to load',
    // WEB-3752 юнит уходы: «Экраны, где уходят больше всего» и черновая карточка
    // «Ответы, после которых уходят» (эталон analytics-x.jsx: TX screensT, scrCols,
    // openMap, scrGo, ansLeaveT, alCols, alGo; FE_AN.screens). Образец строк
    // alRows снят — карточка идёт по ответу «Ответов» (WEB-3740 юнит 53-1).
    screensTitle: 'Screens where most people leave',
    scrColScreen: 'Screen',
    // WEB-4119 (сверка вида 31.10 № 6): подпись экрана с именем — «N. имя», как у эталона
    // (analytics-x.jsx:235); без имени — «Screen N» аудита (audit.screenLabelNumbered).
    scrLabelNamed: (n: string, name: string) => `${n}. ${name}`,
    scrColSaw: 'Saw',
    scrColLeft: 'Left',
    scrColRate: 'Leave rate',
    scrColTime: 'Time on screen',
    screensOpenMap: 'Open on the map',
    screensMapNoFunnel: 'Counted across the whole project: no funnel has screen views yet, so there is no map to open',
    screensRowGo: 'Open the screen on the map',
    screensFunnel: (name: string) => `Funnel: ${name}`,
    screensAutoFunnel: 'most visited',
    screensNone: 'No one has reached the screens yet',
    screensFirstOnly: (dims: string) => `${dims}: only the first one counted`,
    screensLoadFailed: 'Could not load the drop-off by screen',
    ansLeaveTitle: 'Answers people leave after',
    alColAnswer: 'Answer',
    alColPicked: 'Picked',
    alColRate: 'Leave rate',
    ansLeaveGo: 'Open in Answers',
    allMetricsTable: 'All metrics — table',
    // WEB-3752 юнит таблица: каталог показателей «Все показатели» — разделы
    // MSEC, подписи и подсказки MET, тексты TT «Колонок» (эталон analytics-x.jsx
    // версии 777c63dd, стр. 386–427). Подсказка «finished» эталона — заметка
    // разработчику, не переносится.
    metSecTraffic: 'Traffic',
    metSecQuiz: 'Quiz',
    metSecPay: 'Paywall and payment',
    metSecMoney: 'Money',
    metSecApp: 'App',
    metViews: 'Views',
    metUniqueVisitors: 'Uniques',
    metUniqueVisitorsH: 'People: one person in two tabs counts once',
    metUniques: 'Sessions',
    metPageviews: 'Pageviews',
    metTraffback: 'Traffback',
    metTraffbackH: 'Came back from the app to the funnel',
    metCrReg: 'Visit to sign-up',
    metCrRegH: 'Sign-ups / uniques',
    metConvBuy: 'Purchase conversion',
    metConvBuyH: 'Purchases / uniques',
    metQuizStarts: 'Quiz starts',
    metQuizStartRate: 'Quiz start rate',
    metQuiz50: 'Quiz 50%',
    metQuiz90: 'Quiz 90%',
    metFinished: 'Finished the quiz',
    metFinishRate: 'Finished, %',
    metFinishRateH: 'Finished / quiz starts',
    metQuizDrop: 'Left the quiz, %',
    metQuizDropH: 'Started but did not finish',
    metRegistrations: 'Registrations',
    metPaywallViews: 'Paywall',
    metPaywallViewsH: 'Paywall views',
    metPaywallUniques: 'Saw the paywall, people',
    metCheckoutStarts: 'Started checkout',
    metFailedCheckouts: 'Failed payments',
    metFailRate: 'Payment failure, %',
    metFailRateH: 'Failed payments / started checkout',
    metCrPwBuy: 'Paywall to purchase',
    metCrPwBuyH: 'Purchases / saw the paywall',
    metPurchases: 'Purchases',
    metTrials: 'Trials',
    metTrialPaid: 'Trial to paid',
    metRenewals: 'Renewals',
    metRefundsN: 'Refunds, count',
    metRefundsUsd: 'Refunds, $',
    metRefundRate: 'Refund rate',
    metRefundRateH: 'Refunds ÷ all payments (first and renewals), by count',
    metChargebacks: 'Chargebacks',
    metChargebacksH: 'Lost payment disputes',
    metTtp: 'Time to purchase, median',
    metTtpH: 'From first visit to payment',
    metRevenue: 'Revenue',
    metRevenueH: 'First payments',
    metNet: 'Net revenue',
    metNetH: 'First payments and renewals minus refunds and chargebacks',
    metArpu: 'Revenue per visitor',
    metArpuH: 'ARPU — revenue / uniques',
    metArppu: 'Revenue per payer',
    metArppuH: 'ARPPU — revenue / purchases',
    metAvgCheck: 'Average payment',
    metAvgCheckH: 'Revenue with renewals / number of payments',
    metLtv30: 'Revenue per payer, 30 days',
    metLtv30H: 'LTV, 30 days',
    metSpend: 'Ad spend',
    metRoas: 'Ad payback',
    metRoasH: 'ROAS — revenue / spend',
    metCpa: 'Cost per payer',
    metCpaH: 'CPA — spend / purchases',
    metInstalls: 'Installs',
    metConversionToApp: '% to app',
    metConversionToAppH: 'Installs / purchases',
    metActivations: 'Activations',
    metInstAct: 'Install to activation',
    metInstActH: 'Activations / installs',
    allBy: 'Group by',
    allCols: 'Columns',
    allShown: 'Shown',
    allAdd: (section: string) => `Add: ${section.toLowerCase()}`,
    allReset: 'Reset to default',
    allMin1: 'Keep at least one column',
    // WEB-3752 юнит отбор: фильтры таблицы «Все показатели», счётчик строк, выгрузка
    allFAdd: 'Filter',
    allFDims: 'By breakdown',
    allFMets: 'By metric',
    allFDrop: 'Remove',
    allFAll: 'All — remove the filter',
    allFReset: 'Reset',
    allFRowsOf: (shown: number, total: number) => `Rows: ${shown} of ${total}`,
    allFNoRows: 'No row matches the table filters',
    allFGe: 'at least',
    allFLe: 'at most',
    allFCondOp: 'Comparison',
    allFCondV: 'Value',
    allFApply: 'Apply',
    allFMin: 'min',
    allExportWhat: 'the All metrics table',

    // WEB-3752 юнит шапка: «Выгрузить» вкладку, «Спросить ИИ», «Виды» (эталон analytics-x.jsx:220–237)
    exportTabWhat: (lens: string) => `the “${lens}” tab`,
    askPh: 'Ask the data: “where do we lose the most people”, “which country converts best”…',
    askSug1: 'Where do we lose the most people: quiz start, before the paywall, or at checkout?',
    askSug2: 'Which campaign brought the most purchases?',
    askSug3: 'Which country converts visitors to purchases best?',
    views: 'Views',
    viewAll: 'All data',
    viewTikTok: 'TikTok — campaigns, 30 days',
    viewAb: 'A/B tests, 30 days',
    viewMembers: 'By member, 30 days',
    viewChanged: 'changed',
    viewMine: 'Saved',
    viewSaveCurrent: 'Save current view',
    viewTitle: 'Save the view',
    viewName: 'View name',
    viewPh: 'e.g. iOS from TikTok, last week',
    viewDup: 'A view with this name already exists',
    viewSave: 'Save',
    viewCancel: 'Cancel',
    viewSaved: 'View saved',
    // WEB-3752 юнит черновик: образцовые значения черновых разрезов (эталон analytics-dims.jsx:23–25), «затем», разбивка без сервера
    dvLngEn: 'English',
    dvLngDe: 'German',
    dvLngEs: 'Spanish',
    dvLngRu: 'Russian',
    dvDevIphone: 'iPhone',
    dvDevAndroid: 'Android',
    dvDevDesktop: 'Desktop',
    dvDevTablet: 'Tablet',
    dvOsIos: 'iOS',
    dvOsAndroid: 'Android',
    dvOsWindows: 'Windows',
    dvOsMacos: 'macOS',
    allThen: 'Then',
    allThenNone: 'none',
    dimLater: 'The server will start counting this breakdown',
    // WEB-3752 свод доводка: «затем» — раскрыть / свернуть все строки (эталон AX:425)
    allExpandAll: 'Expand all',
    allCollapseAll: 'Collapse all',
  },
  {
    // WEB-2420 Д2 (решение владельца 23.09): «Показы», не «Клики»; денежная
    // колонка — «Выручка», не «Revenue».
    colViews: 'Показы',
    colUniques: 'Сессии',
    colUniqueVisitors: 'Уники',
    colQuizStarts: 'Начали квиз',
    colQuizStartRate: 'Доля начавших квиз',
    colPageviews: 'Просмотры страниц',
    colRegistrations: 'Регистрации',
    colPurchases: 'Покупки',
    colFailedCheckouts: 'Неудачные оплаты',
    colRevenue: 'Выручка',
    colQuiz50: 'Квиз 50%',
    colQuiz90: 'Квиз 90%',
    colPaywallViews: 'Пейволл',
    colInstalls: 'Установки',
    colTraffback: 'Траффбэк',
    colArpu: 'ARPU',
    colArppu: 'ARPPU',
    colConvBuy: 'Конверсия в покупку',
    orgColVisitors: 'Посетители',
    orgColConversion: 'Конверсия',
    colConversionToApp: '% в приложение',

    columns: 'Колонки',
    cohortsHiddenOwnMoney:
      'Когорты скрыты: считаются по покупателям. Изменить — в настройках проекта.',
    derivedColumnsToggle: 'ARPU / ARPPU / конверсия',
    columnsOwnMoneyNote:
      'Денежных колонок в списке нет: платежи у вас свои. Изменить — в настройках проекта.',
    columnsReorderHint:
      'Перетащите строки, чтобы изменить порядок. CSV выгружает ровно видимые колонки в этом порядке.',
    dragToSort: (label: string) => `Перетащить для сортировки: ${label}`,

    directTrafficNoUtm: 'Прямой трафик / без UTM',
    rowNoFunnel: 'Без воронки',
    funnelRowsOverTotal:
      'Один пейволл может стоять в нескольких воронках: его посетители и оплаты входят в строку каждой, поэтому сумма строк может быть больше «Итого».',
    rowNoChannel: 'Без канала',
    todNight: 'Ночь, 0–6',
    todMorning: 'Утро, 6–12',
    todAfternoon: 'День, 12–18',
    todEvening: 'Вечер, 18–24',
    rowKindApex: (_name: string) => 'Основной домен',
    rowKindUnattributed: (_name: string) => 'Не атрибутировано',
    rowKindDirect: (_name: string) => 'Прямой',
    rowKindDeletedVariant: (id: string) => `Удалённый вариант (${id})`,
    rowKindDeletedSmartlink: (id: string) => `Удалённый A/B-тест (${id})`,
    rowKindDeletedDomain: (id: string) => `Удалённый домен (${id})`,
    rowKindDeletedMember: (id: string) => `Удалённый участник (${id})`,
    rowKindQuiz: (name: string) => `Квиз «${name}»`,
    rowKindWholeFunnel: (name: string) => `Воронка «${name}»`,
    rowKindUrl: (address: string) => `URL ${address}`,
    dimension: 'Измерение',
    total: 'Итого',
    refresh: 'Обновить',
    exportCsv: 'Экспорт CSV',
    statsLoadFailed: 'Не удалось загрузить статистику',
    noDataYet: 'Пока нет данных',
    noDataYetHint: 'Опубликуйте воронку и запустите трафик',
    trafficEmptyTitle: 'Данные появятся, когда пойдёт трафик',

    funnelLoadFailed: 'Не удалось загрузить воронку',
    funnelStepQuizView: 'Открыли воронку',
    funnelStepQuizStart: 'Начали квиз',
    funnelStepQuizComplete: 'Прошли квиз',
    funnelStepPaywallView: 'Увидели экран цен',
    funnelStepCheckoutStart: 'Начали оплату',
    funnelStepCheckoutSuccess: 'Оплатили',
    funnelStepInstalls: 'Установки',
    funnelStepPaywallHandoff: 'Ушли на оплату',
    funnelMoneyStepsHidden:
      'Шаги экрана цен, оплаты и установок скрыты: платежи у вас свои. Последнее, что мы можем измерить, — момент, когда мы увели человека на вашу страницу оплаты.',
    funnelCaption: 'Воронка за период и выбранные фильтры · уникальных сессий:',
    convFromPrevStep: 'Конверсия от предыдущего шага',
    peopleWord: (n: number) =>
      plural('ru', n, { one: 'человек', few: 'человека', many: 'человек' }),
    ofPrevStep: (pct: string) => `${pct}% от пред. шага`,

    presetToday: 'Сегодня',
    presetLast7Days: 'Последние 7 дней',
    presetLast30Days: 'Последние 30 дней',
    presetAllTime: 'Всё время',
    presetLast7DaysShort: '7 дней',
    presetLast30DaysShort: '30 дней',
    rangeStart: 'Начало',
    rangeEnd: 'Конец',

    countryName: (code: string) =>
      RUSSIAN_COUNTRY_NAMES.get(code) ?? ENGLISH_COUNTRY_NAMES.get(code) ?? code,
    groupNone: 'Без группировки',
    groupCountry: 'Страна',
    groupDate: 'Дата',
    groupUtmSource: 'UTM-источник',
    groupUtmCampaign: 'UTM-кампания',
    groupQuiz: 'Квиз',
    groupPaywall: 'Пейволл',
    groupDomain: 'Домен',
    groupMember: 'Участник',
    groupVariant: 'Вариант',
    groupSmartLink: 'A/B-тест',
    dimFunnel: 'Воронка',
    dimQuiz: 'Квиз',
    dimPaywall: 'Пейволл',
    dimUtmSource: 'UTM-источник',
    dimUtmCampaign: 'UTM-кампания',
    dimUtmMedium: 'UTM-канал',
    dimCountry: 'Страна',
    dimDomain: 'Домен',
    dimMember: 'Участник',
    dimVariant: 'Вариант A/B',
    dimAbTest: 'A/B-тест',
    dimLanguage: 'Язык',
    dimDevice: 'Устройство',
    dimOs: 'Система',
    dimTimeOfDay: 'Время суток',
    dimDay: 'День',
    // WEB-3752 юнит 2: шапка, пустая аналитика и общая панель фильтров
    // (эталон analytics-x.jsx, TX и FV).
    lensFlow: 'Воронка',
    lensGroupLabel: 'Линза',
    emptyTitle: 'Данных пока нет',
    emptyText: 'Аналитика появится после первых посетителей воронки',
    emptyBuild: 'Собрать воронку',
    filterTestPayments: 'Тестовые оплаты',
    filterTestExcluded: 'без них',
    filterTestIncluded: 'вместе с ними',
    filterMore: 'Ещё фильтры',
    filterLess: 'Скрыть пустые',
    filterReset: 'Сбросить',
    filterAnswerRemove: 'Убрать фильтр',
    fieldPeriod: 'Период',
    fieldUtmSource: 'UTM-источник',
    fieldUtmCampaign: 'UTM-кампания',
    fieldUtmMedium: 'UTM-канал',
    fieldGroupBy: 'Группировать по',
    fieldQuizzes: 'Квизы',
    fieldPaywalls: 'Пейволлы',
    fieldMembers: 'Участники',
    fieldAbTests: 'A/B-тесты',
    fieldCountries: 'Страны',
    fieldDomains: 'Домены',
    fieldProjects: 'Проекты',
    fieldFunnels: 'Воронки',
    fieldSources: 'Источники',
    cohortSourcesEmpty: 'Размеченного трафика ещё не было',
    cohortOptionsEmpty: 'В проекте пока ничего нет',
    cohortCountriesEmpty: 'Визитов с известной страной ещё не было',
    cohortRoiFilteredNote:
      'Расход введён на весь трафик, поэтому окупаемость среза не считается. Сбросьте фильтры, чтобы увидеть ROI.',
    cohortSpendNoCurrencyNote:
      'Расход и ROI скрыты: валюта выручки неизвестна или их несколько, сравнить её с одной суммой расхода нельзя.',
    cohortSpendPerCurrencyNote:
      'Выручка в этом отчёте — в нескольких валютах. Расход вводится отдельно для каждой валюты, и ROI сравнивает его только с выручкой в этой валюте.',
    allQuizzes: 'Все квизы',
    allPaywalls: 'Все пейволлы',
    allMembers: 'Все участники',
    allAbTests: 'Все A/B-тесты',
    allCountries: 'Все страны',
    allDomains: 'Все домены',
    allFunnels: 'Все воронки',
    allProjects: 'Все проекты',
    all: 'Все',
    showTestPayments: 'Показывать тестовые оплаты',
    filtersCombineWithAnd: 'Фильтры комбинируются как И (AND).',

    quickReports: 'Быстрые отчёты',
    reportCountries30: 'Страны за 30 дней',
    reportUtmCampaigns7: 'UTM-кампании за 7 дней',
    reportSmartLinks30: 'A/B-тесты за 30 дней',
    reportMembers30: 'По участникам за 30 дней',
    viewGeneral: 'Общее',
    viewDaily: 'По дням',


    monthShort: (index: number) =>
      ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'][index] ?? '',
    monthFull: (index: number) =>
      [
        'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
      ][index] ?? '',

    metricRevenue: 'Выручка',
    metricBuyers: 'Покупатели',
    metricVisitors: 'Посетители',
    metricQuizStarts: 'Начали квиз',
    metricLeftEmail: 'Оставили email',
    metricFinishedQuiz: 'Завершили квиз',
    metricCheckoutStarts: 'Начали оплату',
    metricPaywallHandoff: 'Ушли на оплату',
    metricPaywallHandoffHint:
      'Сколько человек мы увели на вашу страницу оплаты. Это последний шаг, ' +
      'который мы видим: что происходит дальше у вас — мы не считаем.',
    baseMetrics: 'Базовые метрики',
    conversions: 'Конверсии',
    convVisitorToQuizStart: 'Посетитель → Начал квиз',
    convVisitorToQuizFinish: 'Посетитель → Завершил квиз',
    convQuizStartToFinish: 'Начал → Завершил квиз',
    convPaywallToCheckout: 'Пейволл → Начал оплату',
    convCheckoutToSubscription: 'Начал оплату → Подписка',
    convPaywallToSubscription: 'Пейволл → Подписка',
    convVisitorToPurchase: 'Посетитель → Покупка',
    convFinishToHandoff: 'Завершил квиз → Ушёл на оплату',
    totalLtv: 'Общий LTV',
    totalLtvHint:
      'Сколько денег приносит один человек когорты за всю её жизнь ' +
      '(по когортной матрице: продления и возвраты учтены, окно ' +
      'поступлений не ограничено выбранным периодом). С «Выручкой» за ' +
      'период совпадать не обязано.',
    retention: 'Удержание',
    leadsScopedNotice:
      'Показаны только лиды с ваших квизов и пейволлов; заявки без привязки ' +
      'к ресурсу видны владельцу проекта, администратору и менеджеру ' +
      'по удержанию. Выгрузка CSV — по этой же суженной выдаче. Счётчики выше ' +
      'сужены так же, но считаются за последние 30 дней, а подписки участнику ' +
      'не показываются вовсе — с «всего» под таблицей они сходиться не обязаны.',
    leadsExportScopedNotice:
      'Выгрузка сужена до ваших лидов: заявки с чужих квизов и пейволлов, ' +
      'а также без привязки к ресурсу, в файл не попали.',
    leadsExported: 'Лиды экспортированы',
    leadsLoadFailed: 'Не удалось загрузить лиды',
    firstTouchTooltip:
      'First-touch: сохраняется первый заход лида; последующие визиты UTM не перезаписывают',
    leadQuiz: 'Квиз',
    leadPaywall: 'Пейволл',
    leadCreated: 'Создан',
    leadLastVisit: 'Последний визит',
    leadSearchPlaceholder: 'Поиск по email (частичное совпадение)',
    leadsEmptyHint: 'Залейте показы на воронку — первые лиды соберутся автоматически.',

    subscriptionsNoAccess:
      'Нет доступа к сводке по подпискам. Раздел доступен владельцам, администраторам и RM проекта.',
    subscriptionsLoadFailed: 'Не удалось загрузить сводку по подпискам',
    subscriptionsWholeProjectNote: 'По всему проекту, без фильтров',
    subscriptions: 'Подписки',
    subscriptionsNew: 'Новые подписки',
    subscriptionsActive: 'Активные подписки',
    subscriptionsCanceled: 'Отменённые подписки',
    subscriptionsRefunded: 'Возвраты подписок',

    cohortsLoadFailed: 'Не удалось загрузить когорты',
    cohortGranularity: 'Гранулярность',
    cohortGranularityDay: 'День',
    cohortGranularityWeek: 'Неделя',
    cohortGranularityMonth: 'Месяц',
    cohortMetric: 'Метрика',
    cohortMetricLtv: 'LTV на юзера',
    cohortMetricRetention: 'Удержание',
    cohortMetricConversion: 'Конверсия',
    cohortView: 'Вид',
    cohortViewTable: 'Таблица',
    cohortViewChart: 'График',
    cohortHistoryHint: 'История хранится 12 месяцев · фильтры комбинируются как И (AND).',
    cohortsIntro:
      'Окупаемость трафика: когорты по дате первого касания. Расход и ROI — по каждой ' +
      'когорте (расход сохраняется в проекте — его видят и коллеги).',
    cohortsEmptyHint: 'Залейте показы на воронку — первые когорты соберутся автоматически.',
    cohortCurvesAria: 'Кривые когорт по возрасту',
    cohortNotOldEnough: 'Когорта ещё не достигла этого возраста',
    cohort: 'Когорта',
    cohortSize: 'Размер',
    cohortRefundsNote:
      'Возвраты уже вычтены из LTV — повторно их считать не надо. Отдельной колонкой они не приходят: сервер не отдаёт возвраты в разрезе когорты.',
    cohortSpend: 'Расход',
    cohortSpendTooltip:
      'Бюджет, открученный на трафик этой когорты (вводится вручную, сохраняется в проекте — его видят и коллеги)',
    cohortRoiTooltip: '(зрелый LTV/юзер x размер когорты) / расход',
    cohortAverage: 'Среднее',
    cohortHeatLegend: 'Заливка: ярче = выше',
    cohortNotLivedYet: 'ещё не дожили',
    cohortDayZero: 'День 0',
    cohortWeekAge: (n: number) => `Н${n}`,

    orgVisibleColumns: 'Видимые колонки',
    orgProject: 'Проект',
    orgEmptyHint: 'Залейте показы на воронки проектов — сводка соберётся автоматически.',
    orgFilterEmptyTitle: 'По этому фильтру ничего нет',
    orgFilterEmptyHint: 'Измените или сбросьте фильтры выше.',
    orgOwnMoneyNote: (n: number) =>
      `Покупки проектов с оплатой у себя (${n}) здесь не учитываются — посетители этих проектов при этом входят в конверсии и ARPU.`,
    orgUniquesHint:
      'Люди, у которых есть идентификатор посетителя, плюс сессии просмотра квиза ' +
      'или экрана с ценами без него (посетитель не дал согласия на сбор). ' +
      'Служебные сессии в счёт не идут: каждый переход по ссылке A/B-теста заводит ' +
      'отдельную сессию, и без этой поправки один человек, перешедший дважды, ' +
      'посчитался бы за двоих.',
    orgAnalyticsNoAccess:
      'Нет доступа к аналитике. Раздел доступен только владельцам и администраторам организации.',
    orgSwitchProjectFailed: 'Не удалось переключить проект',
    orgDashboard: 'Дашборд',
    revenueHeaderTooltip:
      'Строки показывают поступления первых оплат по разрезу. «Итого» — чистая выручка проекта с учётом продлений и вычетов.',
    revenueHintFirstPaymentsOnly: 'Первые оплаты за выбранный период.',
    revenueHintNoRenewalsInSlice:
      'В этом срезе показаны только первые оплаты: продления и возвраты ' +
      'приходят без страны, UTM, квиза и участника — разложить их по срезу нечем.',
    revenueHintNet:
      'Посчитано за выбранный период: первые оплаты + продления − возвраты ' +
      '− проигранные споры. Открытый спор из выручки не вычитается, пока не ' +
      'решён. С «Общим LTV» не сходится по определению — там деньги когорты ' +
      'за всю её жизнь.',
    revenueHintImportedSubscriptions:
      ' Подписки, перенесённые к нам из прежней платёжной системы, в выручку ' +
      'не входят: те оплаты прошли мимо нашей кассы, и засчитывать их значило ' +
      'бы задваивать вашу историю. Сами подписчики при этом видны, и доступ у ' +
      'них работает.',
    revenueHintMixedCurrency:
      ' Знак валюты не показан: в выборке несколько валют (или продаж нет). ' +
      'Суммы в разных валютах не пересчитываются — сложены как есть.',
    revenueTotalVsRowsNote: (renewals: string, deductions: string) =>
      `«Итого» не равно сумме строк: к первым оплатам добавлены продления на ${renewals} ` +
      `и вычтены возвраты и проигранные споры на ${deductions}. Эти деньги приходят ` +
      'без страны, UTM, квиза и участника — разложить их по строкам нечем.',
    revenueTotalVsRowsNoteNoAmounts:
      '«Итого» не равно сумме строк: к первым оплатам добавлены продления, ' +
      'вычтены возвраты и проигранные споры. Эти деньги приходят без страны, ' +
      'UTM, квиза и участника — разложить их по строкам нечем.',
    revenueHintByCurrency:
      ' Деньги в разных валютах показаны отдельно, строкой на валюту: общей ' +
      'суммы нет, суммы не пересчитываются.',
    revenueNoCurrencyCode: 'без кода валюты',
    revenueTotalVsRowsNoteByCurrency: (parts: string) =>
      '«Итого» не равно сумме строк: к первым оплатам добавлены продления, ' +
      `вычтены возвраты и проигранные споры, по валютам — ${parts}. Эти деньги ` +
      'приходят без страны, UTM, квиза и участника — разложить их по строкам нечем.',
    revenueTotalVsRowsCurrencyPart: (currency: string, renewals: string, deductions: string) =>
      `${currency}: продления ${renewals}, вычеты ${deductions}`,
    currencyColumn: 'Валюта',
    cohortPickCurrency: 'Выберите валюту',
    cohortPickCurrencyNote:
      'Выручка когорт в нескольких валютах — выберите валюту выше, чтобы увидеть ' +
      'LTV: суммы разных валют не складываются.',

    pageTitle: 'Аналитика',

    tabAnswers: 'Ответы',
    answersTitle: 'Распределение ответов',
    answersLoading: 'Собираем ответы…',
    answersLoadFailed: 'Не удалось загрузить распределение ответов.',
    answersEmpty: 'За этот период ответов нет',
    answersEmptyHint:
      'Расширьте период или ослабьте фильтры. Ответы появляются, когда ' +
      'посетители проходят экран с вопросом на выбор.',
    answersTruncated:
      'Список обрезан по пределу строк: сузьте период или фильтры, чтобы увидеть остальное.',
    answersColOption: 'Вариант',
    answersColRespondents: 'Ответили',
    answersColShare: 'Доля',
    answersQuestionFallback: (blockId: string) => `Вопрос ${blockId}`,
    answersQuestionTotal: (count: string) => `ответили на этот вопрос: ${count} чел.`,
    // WEB-2566 п.1: подсказка у недоступной выгрузки когорт без выбранной валюты.
    cohortExportPickCurrency: 'Выберите валюту для выгрузки',
    // WEB-3752 юнит 3: река шагов, «Шаги», «Сравнить», «По дням»,
    // «Группировать по», «Обновить» (эталон analytics-x.jsx: TX, ST, TT;
    // подпись «Сегодня» — noHours главной home-x.jsx, решение владельца 27.10 № 11).
    stepsBtn: 'Шаги',
    stepsShown: 'Шаги по порядку',
    stepsAdd: 'Добавить шаг',
    stepsMin: 'Не меньше 3',
    stepsMax: 'Не больше 8',
    stepsColumn: (name: string) => `колонка «${name}»`,
    stepsBar: 'Шаги воронки',
    stepsHint: 'Проценты — от предыдущего шага',
    stepsUp: 'Выше',
    stepsDown: 'Ниже',
    stepsDrag: 'Перетащите, чтобы поменять порядок',
    stepsReset: 'Сбросить к стандартным',
    stepLeftEmail: 'Оставили почту',
    stepColFinished: 'Дошли до конца квиза',
    stepColPaywallUniques: 'Увидели пейволл, люди',
    stepColCheckoutStarts: 'Начали оплату',
    compare: 'Сравнить',
    compareTip: 'Показать прошлый период рядом с текущим',
    breakdownToFilter: 'в фильтр',
    breakdownConv: 'конв.',
    dataRefreshed: 'Данные обновлены',
    // WEB-4119 (сверка вида 31.10 № 3): «Выгрузить» не гаснет, как у эталона; файла нет — эта всплывашка.
    exportNothing: 'Выгружать нечего: на вкладке нет данных или они не загрузились',
    // WEB-3752 юнит уходы: «Экраны, где уходят больше всего» и черновая карточка
    // «Ответы, после которых уходят» (эталон analytics-x.jsx: TX screensT, scrCols,
    // openMap, scrGo, ansLeaveT, alCols, alGo; FE_AN.screens). Образец строк
    // alRows снят — карточка идёт по ответу «Ответов» (WEB-3740 юнит 53-1).
    screensTitle: 'Экраны, где уходят больше всего',
    scrColScreen: 'Экран',
    // WEB-4119 (сверка вида 31.10 № 6): «N. имя», как у эталона (analytics-x.jsx:224).
    scrLabelNamed: (n: string, name: string) => `${n}. ${name}`,
    scrColSaw: 'Видели',
    scrColLeft: 'Ушли',
    scrColRate: 'Доля ухода',
    scrColTime: 'Время на экране',
    screensOpenMap: 'Открыть на карте',
    screensMapNoFunnel: 'Посчитано по всему проекту: ни у одной воронки ещё нет показов экранов, открывать нечего',
    screensRowGo: 'Открыть экран на карте',
    screensFunnel: (name: string) => `Воронка: ${name}`,
    screensAutoFunnel: 'самая посещаемая',
    screensNone: 'Пока никто не дошёл до экранов',
    screensFirstOnly: (dims: string) => `${dims}: учтено только первое`,
    screensLoadFailed: 'Не удалось загрузить отвал по экранам',
    ansLeaveTitle: 'Ответы, после которых уходят',
    alColAnswer: 'Ответ',
    alColPicked: 'Выбрали',
    alColRate: 'Доля ухода',
    ansLeaveGo: 'Открыть в «Ответах»',
    allMetricsTable: 'Все показатели — таблица',
    metSecTraffic: 'Трафик',
    metSecQuiz: 'Квиз',
    metSecPay: 'Пейволл и оплата',
    metSecMoney: 'Деньги',
    metSecApp: 'Приложение',
    metViews: 'Показы',
    metUniqueVisitors: 'Уники',
    metUniqueVisitorsH: 'Люди: один человек в двух вкладках — один',
    metUniques: 'Сессии',
    metPageviews: 'Просмотры страниц',
    metTraffback: 'Траффбэк',
    metTraffbackH: 'Вернулись из приложения обратно в воронку',
    metCrReg: 'Из визита в регистрацию',
    metCrRegH: 'Регистрации / уники',
    metConvBuy: 'Конверсия в покупку',
    metConvBuyH: 'Покупки / уники',
    metQuizStarts: 'Начали квиз',
    metQuizStartRate: 'Доля начавших квиз',
    metQuiz50: 'Квиз 50%',
    metQuiz90: 'Квиз 90%',
    metFinished: 'Дошли до конца квиза',
    metFinishRate: 'Дошли до конца, %',
    metFinishRateH: 'Дошли до конца / начали квиз',
    metQuizDrop: 'Ушли с квиза, %',
    metQuizDropH: 'Начали, но не дошли до конца',
    metRegistrations: 'Регистрации',
    metPaywallViews: 'Пейволл',
    metPaywallViewsH: 'Показы пейволла',
    metPaywallUniques: 'Увидели пейволл, люди',
    metCheckoutStarts: 'Начали оплату',
    metFailedCheckouts: 'Неудачные оплаты',
    metFailRate: 'Отказ оплаты, %',
    metFailRateH: 'Неудачные оплаты / начали оплату',
    metCrPwBuy: 'Из пейволла в покупку',
    metCrPwBuyH: 'Покупки / увидели пейволл',
    metPurchases: 'Покупки',
    metTrials: 'Пробные периоды',
    metTrialPaid: 'Из пробного в платный',
    metRenewals: 'Продления',
    metRefundsN: 'Возвраты, шт',
    metRefundsUsd: 'Возвраты, $',
    metRefundRate: 'Доля возвратов',
    metRefundRateH: 'Возвраты ÷ все оплаты (первые и продления), по числу оплат',
    metChargebacks: 'Чарджбэки',
    metChargebacksH: 'Проигранные споры по оплатам',
    metTtp: 'Время до покупки, медиана',
    metTtpH: 'От первого визита до оплаты',
    metRevenue: 'Выручка',
    metRevenueH: 'Первые оплаты',
    metNet: 'Чистая выручка',
    metNetH: 'Первые оплаты и продления минус возвраты и чарджбэки',
    metArpu: 'Доход с посетителя',
    metArpuH: 'ARPU — выручка / уники',
    metArppu: 'Доход с платящего',
    metArppuH: 'ARPPU — выручка / покупки',
    metAvgCheck: 'Средний чек',
    metAvgCheckH: 'Выручка с продлениями / число оплат',
    metLtv30: 'Доход с платящего за 30 дней',
    metLtv30H: 'LTV за 30 дней',
    metSpend: 'Расход на рекламу',
    metRoas: 'Окупаемость рекламы',
    metRoasH: 'ROAS — выручка / расход',
    metCpa: 'Цена платящего',
    metCpaH: 'CPA — расход / покупки',
    metInstalls: 'Установки',
    metConversionToApp: '% в приложение',
    metConversionToAppH: 'Установки / покупки',
    metActivations: 'Активации',
    metInstAct: 'Из установки в активацию',
    metInstActH: 'Активации / установки',
    allBy: 'Группировать',
    allCols: 'Колонки',
    allShown: 'Показаны',
    allAdd: (section: string) => `Добавить: ${section.toLowerCase()}`,
    allReset: 'Сбросить к стандартным',
    allMin1: 'Нужна хотя бы одна колонка',
    // WEB-3752 юнит отбор: фильтры таблицы «Все показатели», счётчик строк, выгрузка
    allFAdd: 'Фильтр',
    allFDims: 'По разрезу',
    allFMets: 'По показателю',
    allFDrop: 'Убрать',
    allFAll: 'Все — убрать фильтр',
    allFReset: 'Сбросить',
    allFRowsOf: (shown: number, total: number) => `Строк: ${shown} из ${total}`,
    allFNoRows: 'Ни одна строка не подходит под фильтры таблицы',
    allFGe: 'не меньше',
    allFLe: 'не больше',
    allFCondOp: 'Сравнение',
    allFCondV: 'Значение',
    allFApply: 'Применить',
    allFMin: 'мин',
    allExportWhat: 'таблица «Все показатели»',

    // WEB-3752 юнит шапка: «Выгрузить» вкладку, «Спросить ИИ», «Виды» (эталон analytics-x.jsx:220–237)
    exportTabWhat: (lens: string) => `вкладка «${lens}»`,
    askPh: 'Спросите данные: «где мы теряем больше всего людей», «в какой стране лучшая конверсия»…',
    askSug1: 'Где мы теряем больше всего людей: на старте квиза, до пейволла или на оплате?',
    askSug2: 'Какая кампания принесла больше всего покупок?',
    askSug3: 'В какой стране лучшая конверсия из посетителя в покупку?',
    views: 'Виды',
    viewAll: 'Все данные',
    viewTikTok: 'TikTok — кампании за 30 дней',
    viewAb: 'A/B-тесты за 30 дней',
    viewMembers: 'По участникам за 30 дней',
    viewChanged: 'изменён',
    viewMine: 'Сохранённые',
    viewSaveCurrent: 'Сохранить текущий вид',
    viewTitle: 'Сохранить вид',
    viewName: 'Название вида',
    viewPh: 'например, iOS из TikTok за неделю',
    viewDup: 'Вид с таким названием уже есть',
    viewSave: 'Сохранить',
    viewCancel: 'Отмена',
    viewSaved: 'Вид сохранён',
    // WEB-3752 юнит черновик: образцовые значения черновых разрезов (эталон analytics-dims.jsx:23–25), «затем», разбивка без сервера
    dvLngEn: 'Английский',
    dvLngDe: 'Немецкий',
    dvLngEs: 'Испанский',
    dvLngRu: 'Русский',
    dvDevIphone: 'iPhone',
    dvDevAndroid: 'Android',
    dvDevDesktop: 'Компьютер',
    dvDevTablet: 'Планшет',
    dvOsIos: 'iOS',
    dvOsAndroid: 'Android',
    dvOsWindows: 'Windows',
    dvOsMacos: 'macOS',
    allThen: 'Затем',
    allThenNone: 'нет',
    dimLater: 'Разбивку по этому разрезу начнёт считать сервер',
    // WEB-3752 свод доводка: «затем» — раскрыть / свернуть все строки (эталон AX:425)
    allExpandAll: 'Развернуть все',
    allCollapseAll: 'Свернуть все',
  },
);


/** Половина словаря — для мест, где подпись выбирают функцией (реестры колонок). */
export type AnalyticsStrings = typeof analyticsStrings.en;
